import { Field, Form, Formik, useField } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { useAuthenticatedUser, useFeature } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { AutoSave, Callout, GraphQLErrorMessage, Input, Title } from '@treams/treams-ui';

import UserCollaboratorSelector from '../packages/kakapo-ui-kit/components/users/UserCollaboratorsSelector';
import validator from '../packages/kakapo-ui-services/factories/ValidatorFactory';
import { useShareNote } from './hooks/useShareNote';
import { useUpdateNote } from './hooks/useUpdateNote';
import NoteMetaFormFields from './one-on-ones/NoteMetaFormFields';

const TitleField = Title.withComponent(Field);

const NoteDateField = ({ name, disabled }: { name: string; disabled?: boolean }) => {
	const [field, , helpers] = useField<Date>(name);

	return (
		<Flex>
			<Input.DatePicker
				onChange={(date) => {
					helpers.setValue(date ?? new Date());
				}}
				value={field.value}
				disabled={disabled}
			/>
		</Flex>
	);
};

const NotesMetaEditContainer = ({ note }: { note: graphql.Note }) => {
	const { t } = useTranslation();
	const authenticatedUser = useAuthenticatedUser();
	const hasOneOnOnes = useFeature(graphql.FeatureName.OneOnOnes);
	const { updateNote, error: updateError } = useUpdateNote(note);
	const { shareNote, unshareNote, error: shareError } = useShareNote(note);

	const error = updateError ?? shareError;

	const collaborators: graphql.User[] = note.collaborators.edges
		.map(({ node }) => node)
		.sort((a, b) => {
			if (a.id === note.owner.id) {
				return -1;
			}

			if (b.id === note.owner.id) {
				return 1;
			}

			return Math.sign(a.normalizedName.localeCompare(b.normalizedName));
		});

	const isAuthenticatedUserOwner = note.owner.id === authenticatedUser.id;

	return (
		<>
			<Formik
				key={note.id}
				onSubmit={(values) =>
					updateNote({
						title: values.title.trim(),
						date: values.date,
						subjectID: values.subjectID,
					})
				}
				initialValues={{
					title: note.title ?? '',
					date: note.date ? new Date(note.date) : new Date(),
					subjectID: note.subject?.id ?? undefined,
				}}
				validationSchema={validator
					.object({
						title: validator.string(),
						date: validator.string().required(),
						subjectID: validator.string().optional(),
					})
					.required()}
			>
				<Form>
					{!isAuthenticatedUserOwner && !hasOneOnOnes && (
						<Callout
							color="blue"
							title={t('notes.collaborators.sharedIndicationText', {
								ownerFirstName: note.owner.firstName,
							})}
						/>
					)}

					{isAuthenticatedUserOwner && <AutoSave debounceMs={500} />}

					<GraphQLErrorMessage error={error} />

					{hasOneOnOnes ? (
						<NoteMetaFormFields note={note} />
					) : (
						<>
							<Box mt={3}>
								<NoteDateField name="date" disabled={!isAuthenticatedUserOwner} />
							</Box>

							<Box mt={4} mb={3}>
								<TitleField
									type="text"
									placeholder={t('notes.title.placeholder')}
									name="title"
									autoComplete="off"
									disabled={!isAuthenticatedUserOwner}
								/>
							</Box>
						</>
					)}
				</Form>
			</Formik>

			{!hasOneOnOnes && (
				<Box mb={4}>
					<UserCollaboratorSelector
						owner={note.owner}
						collaborators={collaborators}
						onShare={(user) => {
							shareNote(user);
						}}
						onUnshare={(user) => {
							unshareNote(user);
						}}
						disabled={!isAuthenticatedUserOwner}
					/>
				</Box>
			)}
		</>
	);
};

export default NotesMetaEditContainer;
